import React, { useCallback, useEffect, useState } from "react";
import {
  ShieldAlert,
  ClipboardCheck,
  MessageSquareWarning,
  Stamp,
  ArrowRight,
  CheckCircle2,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import {
  fetchWorkflow,
  fetchExceptions,
  waiveException,
  raiseNote,
  clearNote,
  signChecklist,
  transitionEngagement,
  signOffEngagement,
  issueEngagement,
  reopenEngagement,
} from "../../api";

const STATE_LABELS = {
  DRAFT: "Draft",
  AWAITING_DOCUMENTS: "Documents",
  PAYMENT_PENDING: "Payment",
  PROCESSING: "Processing",
  OPEN_ITEMS: "Open Items",
  IN_REVIEW: "In Review",
  READY_TO_ISSUE: "Ready to Issue",
  ISSUED: "Issued",
  REOPENED: "Reopened",
};

const apiError = (e) =>
  (e.response && e.response.data && e.response.data.detail) || "Request failed";

const ExceptionCard = ({ ex, onWaived }) => {
  const [role, setRole] = useState("reviewer");
  const [justification, setJustification] = useState("");

  const submit = async () => {
    const actor = { preparer: "staff_1", reviewer: "reviewer_1", partner: "partner_1" }[role];
    try {
      await waiveException(ex.control_id, role, justification, actor);
      toast.success(`${ex.control_id} waived by ${actor}`, { description: "Waiver recorded on the audit chain." });
      onWaived();
    } catch (e) {
      toast.error(`Waiver rejected for ${ex.control_id}`, { description: apiError(e) });
    }
  };

  return (
    <div className="bg-rose-500/10 border border-rose-500/30 rounded-md p-4 space-y-3" data-testid={`exception-card-${ex.control_id}`}>
      <div className="flex items-center justify-between">
        <span className="font-mono font-black text-rose-400 text-sm">{ex.control_id}</span>
        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/30">
          {ex.severity} · {ex.exceptions} exception{ex.exceptions === 1 ? "" : "s"}
        </span>
      </div>
      <div className="space-y-1 text-[11px] text-zinc-400 max-h-24 overflow-y-auto custom-scrollbar">
        {ex.detail.map((d, i) => (
          <div key={i} className="font-mono bg-zinc-900/70 border border-rose-100 rounded px-2 py-1 truncate">
            {Object.entries(d).map(([k, v]) => `${k}: ${v}`).join(" · ")}
          </div>
        ))}
      </div>
      <div className="flex items-center space-x-2 text-xs">
        <select
          data-testid={`waive-role-select-${ex.control_id}`}
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="bg-zinc-900 border border-white/20 rounded-md px-2 py-1.5 text-xs font-semibold text-zinc-300 cursor-pointer focus:outline-none"
        >
          <option value="preparer">Preparer (staff_1)</option>
          <option value="reviewer">Reviewer (reviewer_1)</option>
          <option value="partner">Partner (partner_1)</option>
        </select>
        <input
          data-testid={`waive-justification-${ex.control_id}`}
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          placeholder="Written justification (required, min length enforced)..."
          className="flex-1 bg-zinc-900 border border-white/20 rounded-md px-2.5 py-1.5 text-xs text-zinc-300 focus:outline-none focus:border-blue-500"
        />
        <button
          data-testid={`waive-btn-${ex.control_id}`}
          onClick={submit}
          className="px-3 py-1.5 bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold rounded-md transition"
        >
          Waive
        </button>
      </div>
    </div>
  );
};

const SignOffView = () => {
  const [wf, setWf] = useState(null);
  const [exceptions, setExceptions] = useState(null);
  const [noteTarget, setNoteTarget] = useState("");
  const [noteText, setNoteText] = useState("");
  const [clearResponses, setClearResponses] = useState({});
  const [signActor, setSignActor] = useState("reviewer_1");
  const [issueActor, setIssueActor] = useState("partner_1");
  const [reopenReason, setReopenReason] = useState("");

  const load = useCallback(() => {
    fetchWorkflow().then(setWf).catch(() => {});
    fetchExceptions().then(setExceptions).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!wf || !exceptions)
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-zinc-400 animate-pulse" data-testid="view-signoff">
        Loading engagement workflow…
      </div>
    );

  const onWf = (res) => setWf(res.workflow);

  const doRaiseNote = async () => {
    if (!noteTarget.trim() || !noteText.trim()) return;
    try {
      const res = await raiseNote(noteTarget, noteText);
      setWf(res.workflow);
      setNoteTarget("");
      setNoteText("");
      toast.success(`Review note #${res.note_id} raised`);
    } catch (e) {
      toast.error("Failed to raise note", { description: apiError(e) });
    }
  };

  const doClearNote = async (id) => {
    try {
      const res = await clearNote(id, clearResponses[id] || "");
      onWf(res);
      toast.success(`Note #${id} cleared`);
    } catch (e) {
      toast.error("Cannot clear note", { description: apiError(e) });
    }
  };

  const doSign = async (item) => {
    try {
      onWf(await signChecklist(item));
      toast.success(`Checklist item signed: ${item}`);
    } catch (e) {
      toast.error("Sign failed", { description: apiError(e) });
    }
  };

  const doTransition = async (target) => {
    try {
      onWf(await transitionEngagement(target));
      toast.success(`Engagement → ${STATE_LABELS[target]}`);
    } catch (e) {
      toast.error("Transition blocked", { description: apiError(e) });
    }
  };

  const doSignOff = async () => {
    try {
      onWf(await signOffEngagement(signActor, "reviewer"));
      toast.success(`Signed off by ${signActor} → Ready to Issue`);
    } catch (e) {
      toast.error("Sign-off blocked", { description: apiError(e) });
    }
  };

  const doIssue = async () => {
    const role = issueActor === "partner_1" ? "partner" : "reviewer";
    try {
      const res = await issueEngagement(issueActor, role);
      setWf(res.workflow);
      toast.success(`Deliverable issued — version ${res.version}`, { description: res.deliverable_hash });
    } catch (e) {
      toast.error("Issuance blocked", { description: apiError(e) });
    }
  };

  const doReopen = async () => {
    try {
      onWf(await reopenEngagement(reopenReason));
      toast.success("Engagement reopened by partner");
    } catch (e) {
      toast.error("Reopen blocked", { description: apiError(e) });
    }
  };

  const currentIdx = wf.states.indexOf(wf.state);

  return (
    <div className="flex-1 overflow-y-auto custom-scrollbar p-8" data-testid="view-signoff">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="border-b border-white/10 pb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white">Review &amp; Sign-Off</h1>
            <p className="text-zinc-400 text-xs mt-1">
              preparer {wf.team.preparer} · reviewer {wf.team.reviewer} · partner {wf.team.partner} — the review gate is
              enforced by the engine, not the UI.
            </p>
          </div>
          <span className="px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs font-bold" data-testid="engagement-state-badge">
            {STATE_LABELS[wf.state]}
            {wf.deliverable_version > 0 && ` · v${wf.deliverable_version}`}
          </span>
        </div>

        {/* STATE PIPELINE */}
        <div className="glass-card rounded-md border border-white/10 p-4 overflow-x-auto">
          <div className="flex items-center space-x-1 min-w-max" data-testid="state-pipeline">
            {wf.states.map((s, i) => (
              <React.Fragment key={s}>
                <span
                  className={`px-2.5 py-1 rounded-md text-[10px] font-bold whitespace-nowrap ${
                    s === wf.state
                      ? "bg-blue-600 text-white"
                      : i < currentIdx
                      ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                      : "bg-white/5 text-zinc-500"
                  }`}
                >
                  {STATE_LABELS[s]}
                </span>
                {i < wf.states.length - 1 && <ArrowRight className="w-3 h-3 text-zinc-600 flex-shrink-0" />}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-white/5 text-[11px] text-zinc-400 flex items-center justify-between">
            <span data-testid="client-status-text">Client view: {wf.client_status.step} — {wf.client_status.ball_in_court}</span>
            <div className="flex items-center space-x-2">
              {wf.legal_next
                .filter((t) => ["PAYMENT_PENDING", "PROCESSING", "OPEN_ITEMS", "IN_REVIEW"].includes(t))
                .map((t) => (
                  <button
                    key={t}
                    data-testid={`transition-btn-${t}`}
                    onClick={() => doTransition(t)}
                    className="px-2.5 py-1 bg-zinc-900 border border-white/10 hover:bg-white/5 rounded-md text-[11px] font-semibold text-zinc-300 transition"
                  >
                    Advance → {STATE_LABELS[t]}
                  </button>
                ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            {/* BLOCKING EXCEPTIONS */}
            <div className="glass-card rounded-md border border-white/10 p-5 space-y-3">
              <h3 className="text-sm font-bold text-white flex items-center border-b border-white/10 pb-3">
                <ShieldAlert className="w-4 h-4 mr-2 text-rose-400" /> Open Blocking Exceptions
                <span className="ml-2 px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-400 text-[10px] font-black" data-testid="exception-count">
                  {exceptions.count}
                </span>
              </h3>
              {exceptions.count === 0 ? (
                <p className="text-xs text-emerald-400 flex items-center" data-testid="no-exceptions-msg">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> No open blocking exceptions — sign-off is not blocked by controls.
                </p>
              ) : (
                <div className="space-y-3">
                  {exceptions.open_blocking.map((ex) => (
                    <ExceptionCard key={ex.control_id} ex={ex} onWaived={load} />
                  ))}
                  <p className="text-[11px] text-zinc-400">
                    A blocking exception can only be waived by a reviewer or partner with a written justification — try
                    the preparer role to see the engine refuse.
                  </p>
                </div>
              )}
            </div>

            {/* REVIEW NOTES */}
            <div className="glass-card rounded-md border border-white/10 p-5 space-y-3">
              <h3 className="text-sm font-bold text-white flex items-center border-b border-white/10 pb-3">
                <MessageSquareWarning className="w-4 h-4 mr-2 text-amber-400" /> Review Notes
              </h3>
              <div className="space-y-2" data-testid="review-notes-list">
                {wf.notes.length === 0 && <p className="text-xs text-zinc-500">No review notes raised.</p>}
                {wf.notes.map((n) => (
                  <div key={n.note_id} className={`rounded-md border p-3 text-xs space-y-2 ${n.open ? "bg-amber-500/10 border-amber-500/30" : "bg-white/[0.04] border-white/10"}`} data-testid={`review-note-${n.note_id}`}>
                    <div className="flex justify-between">
                      <span className="font-bold text-white">#{n.note_id} · {n.target}</span>
                      <span className={`text-[10px] font-bold uppercase ${n.open ? "text-amber-400" : "text-emerald-400"}`}>
                        {n.open ? "Open" : `Cleared by ${n.cleared_by}`}
                      </span>
                    </div>
                    <p className="text-zinc-400">{n.text}</p>
                    {n.open ? (
                      <div className="flex items-center space-x-2">
                        <input
                          data-testid={`clear-note-input-${n.note_id}`}
                          value={clearResponses[n.note_id] || ""}
                          onChange={(e) => setClearResponses((p) => ({ ...p, [n.note_id]: e.target.value }))}
                          placeholder="Documented response required to clear..."
                          className="flex-1 bg-zinc-900 border border-white/20 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:border-blue-500"
                        />
                        <button
                          data-testid={`clear-note-btn-${n.note_id}`}
                          onClick={() => doClearNote(n.note_id)}
                          className="px-3 py-1.5 bg-white hover:bg-zinc-200 text-black text-[11px] font-semibold rounded-md transition"
                        >
                          Clear
                        </button>
                      </div>
                    ) : (
                      <p className="text-[11px] text-zinc-400 italic">Response: {n.response}</p>
                    )}
                  </div>
                ))}
              </div>
              <div className="pt-2 border-t border-white/5 space-y-2">
                <input
                  data-testid="note-target-input"
                  value={noteTarget}
                  onChange={(e) => setNoteTarget(e.target.value)}
                  placeholder="Target (e.g. vendor or line item)"
                  className="w-full bg-zinc-900 border border-white/20 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:border-blue-500"
                />
                <div className="flex items-center space-x-2">
                  <input
                    data-testid="note-text-input"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Note text..."
                    className="flex-1 bg-zinc-900 border border-white/20 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:border-blue-500"
                  />
                  <button
                    data-testid="raise-note-btn"
                    onClick={doRaiseNote}
                    className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-[11px] font-semibold rounded-md transition"
                  >
                    Raise Note
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* CHECKLIST */}
            <div className="glass-card rounded-md border border-white/10 p-5 space-y-3">
              <h3 className="text-sm font-bold text-white flex items-center border-b border-white/10 pb-3">
                <ClipboardCheck className="w-4 h-4 mr-2 text-blue-400" /> Sign-Off Checklist
                <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-black ${wf.checklist_complete ? "bg-emerald-500/10 text-emerald-400" : "bg-white/5 text-zinc-400"}`} data-testid="checklist-status">
                  {wf.checklist.filter((c) => c.signed_by).length}/{wf.checklist.length}
                </span>
              </h3>
              <div className="space-y-1.5" data-testid="checklist-items">
                {wf.checklist.map((c) => (
                  <div key={c.item} className="flex items-center justify-between text-xs py-1.5 border-b border-white/5 last:border-0">
                    <span className={c.signed_by ? "text-zinc-500 line-through" : "text-zinc-300"}>
                      {c.item.replace(/_/g, " ")}
                    </span>
                    {c.signed_by ? (
                      <span className="text-[10px] font-mono text-emerald-400">{c.signed_by} ✓</span>
                    ) : (
                      <button
                        data-testid={`checklist-sign-${c.item}`}
                        onClick={() => doSign(c.item)}
                        className="px-2.5 py-1 bg-zinc-900 border border-white/10 hover:bg-white/5 rounded-md text-[10px] font-semibold text-zinc-400 transition"
                      >
                        Sign
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* SIGN-OFF & ISSUE */}
            <div className="glass-card rounded-md border border-white/10 p-5 space-y-4">
              <h3 className="text-sm font-bold text-white flex items-center border-b border-white/10 pb-3">
                <Stamp className="w-4 h-4 mr-2 text-emerald-400" /> Sign-Off &amp; Issuance
              </h3>

              <div className="space-y-2">
                <div className="text-[11px] text-zinc-400">Reviewer sign-off (requires IN_REVIEW, cleared notes, complete checklist, no blocking exceptions; self-review is blocked):</div>
                <div className="flex items-center space-x-2">
                  <select
                    data-testid="signoff-actor-select"
                    value={signActor}
                    onChange={(e) => setSignActor(e.target.value)}
                    className="bg-zinc-900 border border-white/20 rounded-md px-2 py-1.5 text-xs font-semibold text-zinc-300 cursor-pointer focus:outline-none"
                  >
                    <option value="staff_1">staff_1 (preparer!)</option>
                    <option value="reviewer_1">reviewer_1</option>
                    <option value="partner_1">partner_1</option>
                  </select>
                  <button
                    data-testid="signoff-btn"
                    onClick={doSignOff}
                    className="flex-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-md transition"
                  >
                    Reviewer Sign-Off
                  </button>
                </div>
              </div>

              <div className="space-y-2 pt-2 border-t border-white/5">
                <div className="text-[11px] text-zinc-400">Partner issuance (only a partner may issue a deliverable):</div>
                <div className="flex items-center space-x-2">
                  <select
                    data-testid="issue-actor-select"
                    value={issueActor}
                    onChange={(e) => setIssueActor(e.target.value)}
                    className="bg-zinc-900 border border-white/20 rounded-md px-2 py-1.5 text-xs font-semibold text-zinc-300 cursor-pointer focus:outline-none"
                  >
                    <option value="reviewer_1">reviewer_1 (reviewer!)</option>
                    <option value="partner_1">partner_1</option>
                  </select>
                  <button
                    data-testid="issue-btn"
                    onClick={doIssue}
                    className="flex-1 px-3 py-1.5 bg-white hover:bg-zinc-200 text-black text-xs font-semibold rounded-md transition"
                  >
                    Issue Deliverable
                  </button>
                </div>
              </div>

              {wf.state === "ISSUED" && (
                <div className="space-y-2 pt-2 border-t border-white/5">
                  <div className="text-[11px] text-zinc-400 flex items-center">
                    <RotateCcw className="w-3 h-3 mr-1" /> Partner reopen (documented reason ≥ 15 chars):
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      data-testid="reopen-reason-input"
                      value={reopenReason}
                      onChange={(e) => setReopenReason(e.target.value)}
                      placeholder="Reason for reopening..."
                      className="flex-1 bg-zinc-900 border border-white/20 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:border-blue-500"
                    />
                    <button
                      data-testid="reopen-btn"
                      onClick={doReopen}
                      className="px-3 py-1.5 bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-semibold rounded-md transition"
                    >
                      Reopen
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignOffView;
